<script setup lang="ts">
/**
 * The impacts, risks and opportunities found to be material.
 *
 * A table whose columns nobody would choose freely: what it is, where in the
 * value chain, over what horizon, and what was concluded. Those four are what
 * ESRS asks about every one of them, and a table that left a column out is one
 * that comes back from the assurance provider.
 *
 * Set through the page's own table rules rather than styled here, so it obeys
 * the document's formatting like every other table. The one thing it adds is
 * the mark on the type, because impact, risk and opportunity are read at a
 * glance and a column of three similar words is not read at a glance.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import type { IroSection } from '@/domain/section'

const { t } = useI18n()

const props = defineProps<{ section: IroSection }>()

const rows = computed(() => props.section.entries.filter((entry) => entry.topic.trim() !== ''))
</script>

<template>
  <figure class="iro">
    <table>
      <thead>
        <tr>
          <th>{{ t('inspector.iro.topic') }}</th>
          <th>{{ t('inspector.iro.type') }}</th>
          <th>{{ t('inspector.iro.position') }}</th>
          <th>{{ t('inspector.iro.horizon') }}</th>
          <th>{{ t('inspector.iro.conclusion') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="entry in rows" :key="entry.id">
          <td class="iro__topic">{{ entry.topic }}</td>
          <td>
            <span class="iro__type" :class="`iro__type--${entry.type}`">
              {{ t(`inspector.iro.types.${entry.type}`) }}
            </span>
          </td>
          <td>{{ t(`inspector.iro.positions.${entry.position}`) }}</td>
          <td>{{ t(`inspector.iro.horizons.${entry.horizon}`) }}</td>
          <td>{{ entry.conclusion }}</td>
        </tr>
      </tbody>
    </table>

    <p v-if="rows.length === 0" class="iro__empty">{{ t('inspector.iro.empty') }}</p>

    <figcaption v-if="section.caption" class="page-caption">{{ section.caption }}</figcaption>
  </figure>
</template>

<style scoped>
.iro {
  margin: 0;
}

.iro__topic {
  font-weight: 600;
}

/* Impact, risk and opportunity read at a glance, which a column of three
   similar words does not. A mark rather than a colour alone, so it survives
   being printed in black. */
.iro__type {
  display: inline-flex;
  align-items: center;
  gap: 1.2mm;
  white-space: nowrap;
}

.iro__type::before {
  content: '';
  width: 1.8mm;
  height: 1.8mm;
  border-radius: 50%;
  border: 0.4mm solid var(--paper-ink-soft);
}

.iro__type--impact::before {
  background: var(--paper-ink-soft);
  border-color: var(--paper-ink-soft);
}

.iro__type--risk::before {
  /* Hollow, and squared off: a risk is the one a reader is looking for. */
  border-radius: 0;
  border-color: var(--page-accent);
}

.iro__type--opportunity::before {
  border-radius: 50%;
  border-color: var(--page-accent);
  background: none;
}

.iro__empty {
  margin: 2mm 0 0;
  color: var(--paper-ink-soft);
  font-size: 9pt;
  font-style: italic;
  text-align: left;
}

@media print {
  .iro__type::before {
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
}
</style>
